import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Loader2, Play, RefreshCw, FileDown, Upload, AlertTriangle, Check, X, Undo2, Plus, Trash2, Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { EmptyState, ErrorState, TableSkeleton, StatusPill, money, plural } from '../../components/ui';
import { Notice, Segmented } from '../Financials/financeKit';
import { toCsv } from '../../utils/csv';
import { describeRun } from './silRunSummary';

// SIL weekly claims (routes/silWeekly.js): SIL funding set to Weekly on a client's funding line is
// claimed once a week from the Monday run, not by the hour. This tab runs and reviews those claims,
// builds the NDIA bulk payment request, imports the NDIA results, and holds the SIL reports and
// settings (Vserve "SIL Weekly Claiming and Pay Workflow" v1.3, sections 6 to 12).

const pad = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (iso, n) => { const d = fromIso(iso); d.setDate(d.getDate() + n); return isoOf(d); };
const mondayOf = (iso) => addDays(iso, -((fromIso(iso).getDay() + 6) % 7));
const au = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
const weekLabel = (iso) => (iso ? `${au(iso)} to ${au(addDays(iso, 6))}` : '');
const errOf = (e, fb) => e?.response?.data?.message || fb;

const STATUS_TONE = {
  held: ['warning', 'Held'], not_claimed: ['neutral', 'Not claimed'], ready: ['info', 'Ready to claim'],
  submitted: ['info', 'Submitted'], invoiced: ['info', 'Invoiced'], paid: ['success', 'Paid'],
  rejected: ['danger', 'Rejected'], void: ['neutral', 'Void'],
};
const statusPill = (s) => { const [tone, label] = STATUS_TONE[s] || ['neutral', s]; return <StatusPill tone={tone} label={label} />; };

function downloadText(name, text, type = 'text/csv') {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
// A participant name or note starting with = + - @ would open as an Excel formula; utils/csv.js
// writes those cells as text.
const csvOf = toCsv;

const VIEWS = [
  { value: 'claims', label: 'Claims' }, { value: 'ndia', label: 'NDIA' }, { value: 'forecast', label: 'Forecast' },
  { value: 'pnl', label: 'P&L' }, { value: 'unpaid', label: 'Unpaid' }, { value: 'absences', label: 'Absences' },
  { value: 'settings', label: 'Settings' }, { value: 'audit', label: 'Audit log' },
];

export default function SilWeeklyTab() {
  const [overview, setOverview] = useState(null);
  const [ovError, setOvError] = useState(false);
  const [view, setView] = useState('claims');
  const [runWeek, setRunWeek] = useState(() => addDays(mondayOf(isoOf(new Date())), -7));
  const [busy, setBusy] = useState('');
  const [stamp, setStamp] = useState(0);          // bumps every view after a run
  const [lastRun, setLastRun] = useState(null);   // what the last "Run the claim" did, said in words
  const confirm = useConfirm();

  const loadOverview = useCallback(async () => {
    setOvError(false);
    try { const { data } = await api.get('/sil-weekly/overview'); setOverview(data); } catch { setOvError(true); }
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview, stamp]);

  const doRun = async () => {
    const ok = await confirm(`Claim every Weekly SIL client for the week ${weekLabel(runWeek)}. Weeks already claimed are left as they are.`,
      { title: 'Run the weekly SIL claim?', confirmLabel: 'Run the claim' });
    if (!ok) return;
    setBusy('run');
    try {
      const { data } = await api.post('/sil-weekly/run', { weekStart: runWeek });
      // The outcome stays on the tab, client by client, with what to do next: a toast reading
      // "Claimed 0, held 2" looked as if nothing had happened.
      const d = describeRun(data, weekLabel(runWeek));
      setLastRun(d);
      if (d.tone === 'green') toast.success(d.title); else toast(d.title);
      setStamp((n) => n + 1);
    } catch (e) { toast.error(errOf(e, 'The claim run failed')); }
    finally { setBusy(''); }
  };
  const doRecheck = async () => {
    setBusy('recheck');
    try {
      const { data } = await api.post('/sil-weekly/recheck');
      const n = (data || []).reduce((a, o) => a + (o.claimed || 0) + (o.capped || 0), 0);
      toast.success(data?.length ? `Re-checked ${plural(data.length, 'held week')}: ${n} now claimed` : 'No held weeks to re-check');
      setStamp((x) => x + 1);
    } catch (e) { toast.error(errOf(e, 'Re-check failed')); }
    finally { setBusy(''); }
  };

  const ov = overview;
  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
            <Home size={18} className="text-teal-700" aria-hidden="true" />
          </span>
          <div className="flex-1 min-w-[240px]">
            <h3 className="text-base font-semibold text-slate-900">SIL weekly claims</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-[75ch]">
              SIL funding set to Weekly on a client's funding line is claimed once a week, for the Monday to Sunday week just ended,
              when at least one SIL shift in it is approved. Those shifts show $0.00 and workers are still paid per shift.
              Set a line to Weekly in the client's profile, under Funding.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-slate-100">
          <div>
            <label htmlFor="sil-run-week" className="block text-[12px] font-medium text-slate-600 mb-1">Claim week (Monday)</label>
            <input id="sil-run-week" type="date" className="input-field !w-auto !py-1.5 text-sm" value={runWeek}
              onChange={(e) => e.target.value && setRunWeek(mondayOf(e.target.value))} />
          </div>
          <p className="text-[13px] text-slate-500 pb-2 tabular-nums">{weekLabel(runWeek)}</p>
          <button type="button" className="btn-primary btn-sm" disabled={!!busy} onClick={doRun}>
            {busy === 'run' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Play size={13} aria-hidden="true" />} Run the claim
          </button>
          <button type="button" className="btn-secondary btn-sm" disabled={!!busy} onClick={doRecheck}>
            {busy === 'recheck' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={13} aria-hidden="true" />} Re-check held weeks
          </button>
          {ov && (
            <p className="text-[12px] text-slate-500 pb-2 ml-auto">
              Runs automatically every {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][ov.settings?.claimRunDay ?? 1]} from {pad(ov.settings?.claimRunHour ?? 7)}:00, held weeks every day.
            </p>
          )}
        </div>
      </div>

      {lastRun && (
        <Notice tone={lastRun.tone} title={lastRun.title} onDismiss={() => setLastRun(null)}
          actions={lastRun.needsTimesheets ? <Link to="/timesheets" className="btn-secondary btn-sm">Open Timesheets</Link> : null}>
          <ul className="list-disc pl-5 space-y-0.5">
            {lastRun.lines.map((l) => <li key={l}>{l}</li>)}
          </ul>
          {lastRun.next && <p className="mt-1.5">{lastRun.next}</p>}
        </Notice>
      )}
      {ovError && <ErrorState onRetry={loadOverview} />}
      {ov && <Alerts ov={ov} onChanged={() => setStamp((n) => n + 1)} onView={setView} />}

      <Segmented ariaLabel="SIL weekly views" value={view} onChange={setView}
        options={VIEWS.map((v) => ({ ...v, count: v.value === 'claims' && ov ? (ov.held + ov.ready) || null : v.value === 'ndia' && ov ? ov.readyNdia || null : null }))} />

      {view === 'claims' && <ClaimsView stamp={stamp} onChanged={() => setStamp((n) => n + 1)} />}
      {view === 'ndia' && <NdiaView stamp={stamp} readyNdia={ov?.readyNdia || 0} onChanged={() => setStamp((n) => n + 1)} />}
      {view === 'forecast' && <ForecastView stamp={stamp} />}
      {view === 'pnl' && <PnlView stamp={stamp} />}
      {view === 'unpaid' && <UnpaidView stamp={stamp} />}
      {view === 'absences' && <AbsencesView stamp={stamp} />}
      {view === 'settings' && <SettingsView onSaved={() => setStamp((n) => n + 1)} />}
      {view === 'audit' && <AuditView stamp={stamp} />}
    </div>
  );
}

// ── Alerts: what needs a person today ──────────────────────────────────────────────────────────
function Alerts({ ov, onChanged, onView }) {
  const [busy, setBusy] = useState('');
  const decide = async (id, decision) => {
    setBusy(id);
    try { await api.post(`/sil-weekly/rates/${id}/${decision}`); toast.success(decision === 'approve' ? 'Weekly amount approved' : 'Change declined'); onChanged(); }
    catch (e) { toast.error(errOf(e, 'Could not save the decision')); }
    finally { setBusy(''); }
  };
  const last = ov.lastWeekRun || [];
  const lastTotal = last.filter((r) => ['ready', 'submitted', 'invoiced', 'paid'].includes(r.status)).reduce((a, r) => a + r.total, 0);
  return (
    <div className="space-y-3">
      {!ov.weeklyLines && (
        <Notice tone="slate" title="No client is on Weekly SIL yet">
          Open a client's profile, Funding, and set the SIL funding line's claiming method to Weekly. It needs the service agreement.
        </Notice>
      )}
      {ov.weeklyLines > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Tile label={`Week of ${au(ov.lastWeek)}`} value={money(lastTotal)} sub={last.map((r) => `${r.n} ${STATUS_TONE[r.status]?.[1]?.toLowerCase() || r.status}`).join(' · ') || 'Not run yet'} />
          <Tile label="Held" value={ov.held} tone={ov.held ? 'amber' : null} sub="awaiting approval or review" onClick={() => onView('claims')} />
          <Tile label="Ready to claim" value={ov.ready} sub={`${ov.readyNdia} NDIA-managed`} onClick={() => onView('ndia')} />
          <Tile label="With the NDIA" value={ov.submitted} sub="submitted, awaiting results" onClick={() => onView('unpaid')} />
          <Tile label="Rejected" value={ov.rejected} tone={ov.rejected ? 'red' : null} sub="correct and resubmit" onClick={() => onView('claims')} />
        </div>
      )}
      {ov.filesReady?.length > 0 && (
        <Notice tone="amber" title={`${plural(ov.filesReady.length, 'NDIA claim file')} ready to upload`}
          actions={<button type="button" className="btn-secondary btn-sm" onClick={() => onView('ndia')}>Open NDIA</button>}>
          Built after the weekly claim run: {ov.filesReady.map((f) => `${f.file_name} (${plural(f.lines || 0, 'line')}, ${money(f.total)})`).join(', ')}.
          Download it, then upload it in the myplace provider portal.
        </Notice>
      )}
      {ov.agreementMissing?.length > 0 && (
        <Notice tone="rose" title={`Service agreement missing for ${plural(ov.agreementMissing.length, 'client')} on Weekly SIL`}>
          {ov.agreementMissing.map((a) => `${a.client}${a.service ? `, ${a.service}` : ''} (${a.missing || 'file and date'} missing)`).join('; ')}. Weekly claiming needs a service agreement
          covering weekly claiming and the typical weekly schedule of supports, with its date. Add it in the client's Funding, SIL line, then save.
        </Notice>
      )}
      {ov.pendingApprovals?.length > 0 && (
        <Notice tone="amber" title={`${plural(ov.pendingApprovals.length, 'weekly amount change')} waiting for approval`}>
          <ul className="mt-1 space-y-1.5">
            {ov.pendingApprovals.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{r.client}</span>
                <span>{r.weekly_amount == null ? 'back to the calculated amount' : money(r.weekly_amount)} a week from {au(r.from_week)}</span>
                {r.reason && <span className="text-slate-600">"{r.reason}"</span>}
                <span className="ml-auto flex gap-1.5">
                  <button type="button" className="btn-secondary btn-sm" disabled={busy === r.id} onClick={() => decide(r.id, 'approve')}><Check size={12} aria-hidden="true" /> Approve</button>
                  <button type="button" className="btn-secondary btn-sm" disabled={busy === r.id} onClick={() => decide(r.id, 'decline')}><X size={12} aria-hidden="true" /> Decline</button>
                </span>
              </li>
            ))}
          </ul>
        </Notice>
      )}
      {ov.cappedRecent?.length > 0 && (
        <Notice tone="rose" title={`${plural(ov.cappedRecent.length, 'claim')} capped at the remaining SIL balance`}>
          {ov.cappedRecent.map((c) => `${c.client}, week of ${au(c.week_start)}: ${money(c.amount)} claimed, ${money(c.shortfall)} short`).join('. ')}.
          The shortfall is unfunded. Check the plan with the participant or their plan manager.
        </Notice>
      )}
      {ov.lowBalance?.length > 0 && (
        <Notice tone="amber" title={`${plural(ov.lowBalance.length, 'client')} running low on SIL funding`}>
          {ov.lowBalance.map((l) => `${l.client}: ${money(l.remaining)} left, about ${l.weeksLeft} week${l.weeksLeft === 1 ? '' : 's'} at ${money(l.weekly)}`).join('. ')}.
        </Notice>
      )}
    </div>
  );
}

function Tile({ label, value, sub, tone, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      className={`card p-4 text-left ${onClick ? 'hover:border-indigo-200 transition-colors' : ''} ${tone === 'amber' ? 'border-amber-200 bg-amber-50/40' : tone === 'red' ? 'border-red-200 bg-red-50/40' : ''}`}>
      <p className="text-[12px] font-medium text-slate-500">{label}</p>
      <p className="text-xl font-semibold text-slate-900 tabular-nums mt-0.5">{value}</p>
      {sub && <p className="text-[12px] text-slate-500 mt-0.5 truncate">{sub}</p>}
    </Tag>
  );
}

// ── Claims register (spec 12) ──────────────────────────────────────────────────────────────────
const FILTERS = [
  { value: '', label: 'All' }, { value: 'held', label: 'Held' }, { value: 'ready', label: 'Ready' },
  { value: 'not_claimed', label: 'Not claimed' }, { value: 'submitted,invoiced', label: 'Sent' },
  { value: 'paid', label: 'Paid' }, { value: 'rejected', label: 'Rejected' }, { value: 'void', label: 'Void' },
];
function ClaimsView({ stamp, onChanged }) {
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState(() => addDays(mondayOf(isoOf(new Date())), -7 * 12));
  const [to, setTo] = useState(() => mondayOf(isoOf(new Date())));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState('');
  const confirm = useConfirm();
  const load = useCallback(async () => {
    setError(false);
    try {
      const qs = new URLSearchParams({ from, to, ...(status ? { status } : {}) });
      const { data } = await api.get(`/sil-weekly/claims?${qs}`);
      setRows(data);
    } catch { setError(true); }
  }, [from, to, status]);
  useEffect(() => { load(); }, [load, stamp]);

  const act = async (c, kind) => {
    const text = kind === 'resubmit'
      ? `Send ${c.client}'s claim for the week of ${au(c.week_start)} again as a new request (${money(c.amount)}), once the problem the NDIA reported is fixed.`
      : `${c.client}'s week of ${au(c.week_start)} was held: ${c.reason}. Claim it anyway?`;
    if (!await confirm(text, { title: kind === 'resubmit' ? 'Resubmit the claim?' : 'Claim this week?', confirmLabel: kind === 'resubmit' ? 'Resubmit' : 'Claim it' })) return;
    setBusy(c.id);
    try {
      await api.post(`/sil-weekly/claims/${c.id}/${kind === 'resubmit' ? 'resubmit' : 'claim-anyway'}`);
      toast.success(kind === 'resubmit' ? 'Ready to go in the next NDIA file' : 'Claimed');
      onChanged();
    } catch (e) { toast.error(errOf(e, 'That did not work')); }
    finally { setBusy(''); }
  };
  const exportCsv = () => downloadText(`sil-claims-${from}-to-${to}.csv`, csvOf(rows || [], [
    { label: 'Week', get: (r) => r.week_start }, { label: 'Participant', get: (r) => r.client }, { label: 'NDIS number', get: (r) => r.ndis_number },
    { label: 'Days covered', get: (r) => r.days }, { label: 'Approved SIL shifts', get: (r) => r.approved_shifts },
    { label: 'Claim amount', get: (r) => r.amount }, { label: 'Status', get: (r) => STATUS_TONE[r.status]?.[1] || r.status },
    { label: 'Claim reference', get: (r) => r.claim_reference }, { label: 'Invoice', get: (r) => r.invoice_number }, { label: 'Note', get: (r) => r.message || r.reason },
  ]));

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-end gap-3 p-4 border-b border-slate-100">
        <Segmented size="sm" ariaLabel="Claim status" value={status} onChange={setStatus} options={FILTERS} />
        <div className="flex items-end gap-2">
          <div><label htmlFor="sil-from" className="block text-[12px] text-slate-500 mb-1">Weeks from</label>
            <input id="sil-from" type="date" className="input-field !w-auto !py-1.5 text-sm" value={from} onChange={(e) => e.target.value && setFrom(mondayOf(e.target.value))} /></div>
          <div><label htmlFor="sil-to" className="block text-[12px] text-slate-500 mb-1">to</label>
            <input id="sil-to" type="date" className="input-field !w-auto !py-1.5 text-sm" value={to} onChange={(e) => e.target.value && setTo(mondayOf(e.target.value))} /></div>
        </div>
        <button type="button" className="btn-secondary btn-sm ml-auto" disabled={!rows?.length} onClick={exportCsv}><Download size={13} aria-hidden="true" /> Export</button>
      </div>
      {rows == null && !error ? <TableSkeleton rows={4} cols={7} />
        : error ? <ErrorState onRetry={load} />
        : !rows.length ? <EmptyState icon={Home} title="No weekly SIL claims in these weeks" hint="Run the claim for a week that has ended, or widen the dates." />
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Week</th>
                  <th className="px-4 py-2.5 font-medium">Participant</th>
                  <th className="px-4 py-2.5 font-medium text-right">Days</th>
                  <th className="px-4 py-2.5 font-medium text-right">SIL shifts</th>
                  <th className="px-4 py-2.5 font-medium text-right">Claim</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Invoice / reference</th>
                  <th className="px-4 py-2.5"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.id} className="align-top">
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums">{au(c.week_start)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{c.client}</div>
                      {(c.reason || c.message) && <div className={`text-[12px] mt-0.5 ${c.status === 'rejected' || c.capped ? 'text-red-700' : 'text-slate-500'}`}>{c.message || c.reason}</div>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.days}{c.days < 7 && <span className="text-slate-400 text-[11px] ml-1">part</span>}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {/* As the shifts are now, not when the week was checked. */}
                      <span title="SIL shifts in the week with an approved timesheet">{c.live_approved ?? c.approved_shifts}</span>
                      {/* A paid (settled) week asks for nothing: any timesheet still waiting is in Timesheets › Pending, where pay is approved. */}
                      {(c.live_to_approve ?? 0) > 0 && ['held', 'ready', 'submitted'].includes(c.status) && (
                        <Link to="/timesheets" className="block text-[11px] text-amber-700 hover:underline" title="Shifts in this week whose timesheet is waiting for approval (Pending or Review). Approve them in Timesheets so the workers are paid.">
                          +{c.live_to_approve} to approve
                        </Link>
                      )}
                      {/* Only on a week still open: on a paid week the shifts are history (often done and paid before Rostery), and completing them now would create timesheets and pay twice. */}
                      {(c.live_not_done ?? 0) > 0 && ['held', 'ready', 'submitted'].includes(c.status) && (
                        <Link to="/scheduler" className="block text-[11px] text-rose-700 hover:underline" title="Shifts in this week never completed: no timesheet, so the workers are not paid for them. Complete them in the Scheduler, or cancel them if they did not happen.">
                          +{c.live_not_done} not completed
                        </Link>
                      )}
                      {c.live_approved == null && c.pending_shifts > 0 && ['held', 'ready', 'submitted'].includes(c.status) && <span className="text-amber-700 text-[11px] ml-1">+{c.pending_shifts} pending</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                      {c.amount ? money(c.amount) : '–'}
                      {c.capped && <div className="text-[11px] font-normal text-red-700">capped, {money(c.shortfall)} short</div>}
                      {c.paid_amount != null && c.paid_amount < c.amount && <div className="text-[11px] font-normal text-red-700">paid {money(c.paid_amount)}</div>}
                    </td>
                    <td className="px-4 py-3">{statusPill(c.status)}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {c.invoice_number && <Link to="/invoices?tab=invoices" className="text-indigo-700 hover:text-indigo-900 font-medium">{c.invoice_number}</Link>}
                      {c.claim_reference && <div className="font-mono text-[11px] text-slate-400">{c.claim_reference}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {c.status === 'held' && !/Awaiting shift approval/.test(c.reason || '') && (
                        <button type="button" className="btn-secondary btn-sm" disabled={busy === c.id} onClick={() => act(c, 'claim')}>Claim anyway</button>
                      )}
                      {c.status === 'rejected' && (
                        <button type="button" className="btn-secondary btn-sm" disabled={busy === c.id} onClick={() => act(c, 'resubmit')}><Undo2 size={12} aria-hidden="true" /> Resubmit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ── NDIA bulk payment request + results file (spec 6.2) ────────────────────────────────────────
function NdiaView({ stamp, readyNdia, onChanged }) {
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState('');
  const [errors, setErrors] = useState(null);
  const [recon, setRecon] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get('/sil-weekly/ndia-files'); setFiles(data); } catch { setFiles([]); } }, []);
  useEffect(() => { load(); }, [load, stamp]);

  const build = async () => {
    setBusy('build'); setErrors(null);
    try {
      const { data } = await api.post('/sil-weekly/ndia-file', {});
      downloadText(data.fileName, data.csv);
      toast.success(`${data.fileName}: ${plural(data.lines, 'line')}, ${money(data.total)}. Upload it in the myplace provider portal.`, { duration: 8000 });
      onChanged(); load();
    } catch (e) {
      if (e.response?.status === 422) setErrors(e.response.data.errors || []);
      toast.error(errOf(e, 'Could not build the file'));
    } finally { setBusy(''); }
  };
  const getFile = async (f) => {
    try {
      const { data } = await api.post(`/sil-weekly/ndia-files/${f.id}/download`);
      downloadText(data.fileName, data.csv);
      if (f.status === 'ready') { toast.success('Its claims are now Submitted. Upload the file in the myplace provider portal.'); onChanged(); }
      load();
    } catch { toast.error('Could not download the file'); }
  };
  const importResults = async (file) => {
    if (!file) return;
    setBusy('recon'); setRecon(null);
    try {
      const csv = await file.text();
      const { data } = await api.post('/sil-weekly/reconcile', { csv });
      setRecon(data);
      toast.success(`${data.paid} paid, ${data.rejected} rejected`);
      onChanged();
    } catch (e) { toast.error(errOf(e, 'Could not read that file')); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-3">
        <h4 className="text-sm font-semibold text-slate-900">1. Bulk payment request</h4>
        <p className="text-sm text-slate-600 max-w-[75ch]">
          Every NDIA-managed claim line not yet sent: the SIL weekly lines (quantity 1, Monday to Sunday or the covered days), irregular SIL
          (claim type IRSS) and that week's hourly lines. Each line is checked first: inside the plan, no future dates, not older than two years.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-primary btn-sm" disabled={!!busy || !readyNdia} onClick={build}>
            {busy === 'build' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <FileDown size={13} aria-hidden="true" />}
            Build the claim file
          </button>
          <span className="text-[13px] text-slate-500">{readyNdia ? `${plural(readyNdia, 'SIL weekly claim')} ready` : 'Nothing waiting'}</span>
        </div>
        {errors?.length > 0 && (
          <Notice tone="rose" title={`${plural(errors.length, 'line')} cannot be claimed yet`}>
            <ul className="list-disc pl-5 space-y-0.5">
              {errors.map((e, i) => <li key={i}>{e.participant || ''} {e.invoice_number ? `(${e.invoice_number})` : ''}: {(e.problems || []).join(' ')}</li>)}
            </ul>
          </Notice>
        )}
      </div>

      <div className="card p-5 space-y-3">
        <h4 className="text-sm font-semibold text-slate-900">2. NDIA results file</h4>
        <p className="text-sm text-slate-600 max-w-[75ch]">
          Download the bulk payment results from the portal and import it here. Each line is matched by its claim reference and marked
          Paid or Rejected. A rejected line goes back to the SIL balance; correct it, resubmit it from Claims, and it goes in the next file.
        </p>
        <label className="btn-secondary btn-sm cursor-pointer w-fit">
          {busy === 'recon' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Upload size={13} aria-hidden="true" />} Import results (.csv)
          <input type="file" accept=".csv,text/csv" className="sr-only" disabled={!!busy} onChange={(e) => { importResults(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {recon && (
          <Notice tone={recon.rejected || recon.shortPaid?.length ? 'amber' : 'slate'} title={`${recon.paid} paid, ${recon.rejected} rejected${recon.already ? `, ${recon.already} already paid` : ''}`}>
            {recon.shortPaid?.length > 0 && <p>Paid less than claimed: {recon.shortPaid.map((s) => `${s.reference} (${money(s.paid)} of ${money(s.claimed)})`).join(', ')}.</p>}
            {recon.unmatched?.length > 0 && <p>Not ours (no claim with that reference): {recon.unmatched.join(', ')}.</p>}
          </Notice>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Files built</div>
        {files == null ? <TableSkeleton rows={2} cols={4} /> : !files.length ? <p className="p-4 text-sm text-slate-500">No files yet.</p> : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {files.map((f) => (
                <tr key={f.id}>
                  <td className="px-4 py-2.5 font-mono text-[13px]">{f.file_name}
                    {f.status === 'ready' && <span className="ml-2"><StatusPill tone="warning" label="Ready to upload" /></span>}</td>
                  <td className="px-4 py-2.5 text-slate-600">{plural(f.lines || 0, 'line')}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{money(f.total)}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-[12px]">{new Date(f.created_at).toLocaleString('en-AU')}{f.by ? `, ${f.by}` : ''}</td>
                  <td className="px-4 py-2.5 text-right"><button type="button" className="btn-secondary btn-sm" onClick={() => getFile(f)}><Download size={12} aria-hidden="true" /> Download</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── SIL funding forecast (spec 12) ─────────────────────────────────────────────────────────────
function ForecastView({ stamp }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => { setError(false); try { const { data } = await api.get('/sil-weekly/forecast'); setRows(data); } catch { setError(true); } }, []);
  useEffect(() => { load(); }, [load, stamp]);
  if (error) return <ErrorState onRetry={load} />;
  if (rows == null) return <TableSkeleton rows={3} cols={7} />;
  if (!rows.length) return <EmptyState icon={Home} title="No Weekly SIL funding" hint="Set a client's SIL funding line to Weekly in their profile." />;
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
          <tr>
            <th className="px-4 py-2.5 font-medium">Participant</th><th className="px-4 py-2.5 font-medium">Funding period</th>
            <th className="px-4 py-2.5 font-medium text-right">Funding</th><th className="px-4 py-2.5 font-medium text-right">Claimed</th>
            <th className="px-4 py-2.5 font-medium text-right">Remaining</th><th className="px-4 py-2.5 font-medium text-right">Weekly</th>
            <th className="px-4 py-2.5 font-medium text-right">Still scheduled</th><th className="px-4 py-2.5 font-medium text-right">Projected variance</th>
            <th className="px-4 py-2.5 font-medium text-right">Unfunded (capped)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.lineId}>
              <td className="px-4 py-3 font-medium text-slate-900">{r.client}</td>
              <td className="px-4 py-3 text-slate-600 whitespace-nowrap tabular-nums">{au(r.periodFrom)} to {au(r.periodTo)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{money(r.funding)}{r.openingUsed > 0 && <div className="text-[11px] text-slate-400">{money(r.openingUsed)} used before</div>}</td>
              <td className="px-4 py-3 text-right tabular-nums">{money(r.claimed)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-semibold">{r.remaining == null ? '–' : money(r.remaining)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{r.weekly == null ? '–' : money(r.weekly)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{money(r.scheduledLeft)}<div className="text-[11px] text-slate-400">{plural(r.weeksScheduled, 'week')}</div></td>
              <td className={`px-4 py-3 text-right tabular-nums font-semibold ${r.projectedVariance < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                {r.projectedVariance == null ? '–' : money(r.projectedVariance)}
                <div className="text-[11px] font-normal text-slate-400">{r.projectedVariance < 0 ? 'short' : 'unallocated'}</div>
              </td>
              <td className={`px-4 py-3 text-right tabular-nums ${r.shortfall > 0 ? 'text-red-700 font-semibold' : 'text-slate-400'}`}>{r.shortfall > 0 ? money(r.shortfall) : '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── SIL P&L (spec 11) ──────────────────────────────────────────────────────────────────────────
function PnlView({ stamp }) {
  const [to, setTo] = useState(() => addDays(mondayOf(isoOf(new Date())), -1));
  const [from, setFrom] = useState(() => addDays(mondayOf(isoOf(new Date())), -7 * 8));
  const [group, setGroup] = useState('week');
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => { setError(false); try { const { data: d } = await api.get(`/sil-weekly/pnl?from=${from}&to=${to}`); setData(d); } catch { setError(true); } }, [from, to]);
  useEffect(() => { load(); }, [load, stamp]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const fyOf = (w) => { const y = Number(w.slice(0, 4)); return Number(w.slice(5, 7)) >= 7 ? `${y}-${String(y + 1).slice(2)}` : `${y - 1}-${String(y).slice(2)}`; };
    const keyOf = (r) => (group === 'week' ? `${r.week}|${r.client}` : group === 'client' ? r.client : group === 'house' ? (r.house || 'No house')
      : group === 'fy' ? fyOf(r.week) : r.week.slice(0, 7));
    const m = new Map();
    for (const r of data.rows) {
      const k = keyOf(r);
      const g = m.get(k) || { key: k, label: group === 'week' ? `${au(r.week)} · ${r.client}` : group === 'month' ? fromIso(`${k}-01`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }) : group === 'fy' ? `Financial year ${k}` : k,
        income: 0, silCost: 0, pendingIncome: 0, unfundedSilCost: 0, irregularIncome: 0, irregularCost: 0, otherIncome: 0, otherCost: 0, status: r.claimStatus };
      for (const f of ['income', 'silCost', 'pendingIncome', 'unfundedSilCost', 'irregularIncome', 'irregularCost', 'otherIncome', 'otherCost']) g[f] += r[f] || 0;
      m.set(k, g);
    }
    return [...m.values()];
  }, [data, group]);

  const exportCsv = () => downloadText(`sil-pnl-${from}-to-${to}.csv`, csvOf(data?.rows || [], [
    { label: 'Week', get: (r) => r.week }, { label: 'Participant', get: (r) => r.client }, { label: 'House', get: (r) => r.house },
    { label: 'SIL income', get: (r) => r.income }, { label: 'SIL income received', get: (r) => r.incomeReceived }, { label: 'Pending (held)', get: (r) => r.pendingIncome },
    { label: 'SIL worker cost', get: (r) => r.silCost }, { label: 'SIL margin', get: (r) => r.silMargin }, { label: 'Unfunded SIL cost', get: (r) => r.unfundedSilCost },
    { label: 'Irregular SIL income', get: (r) => r.irregularIncome }, { label: 'Irregular SIL cost', get: (r) => r.irregularCost },
    { label: 'Other services income', get: (r) => r.otherIncome }, { label: 'Other services cost', get: (r) => r.otherCost },
  ]));
  const t = data?.totals;
  const pct = (m, i) => (i > 0 ? `${Math.round((m / i) * 1000) / 10}%` : '–');

  return (
    <div className="space-y-3">
      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div><label htmlFor="pnl-from" className="block text-[12px] text-slate-500 mb-1">Weeks from</label>
          <input id="pnl-from" type="date" className="input-field !w-auto !py-1.5 text-sm" value={from} onChange={(e) => e.target.value && setFrom(mondayOf(e.target.value))} /></div>
        <div><label htmlFor="pnl-to" className="block text-[12px] text-slate-500 mb-1">to</label>
          <input id="pnl-to" type="date" className="input-field !w-auto !py-1.5 text-sm" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} /></div>
        <Segmented size="sm" ariaLabel="Group by" value={group} onChange={setGroup}
          options={[{ value: 'week', label: 'Client and week' }, { value: 'client', label: 'Client' }, { value: 'house', label: 'House' }, { value: 'month', label: 'Month' }, { value: 'fy', label: 'Financial year' }]} />
        <button type="button" className="btn-secondary btn-sm ml-auto" disabled={!data?.rows?.length} onClick={exportCsv}><Download size={13} aria-hidden="true" /> Export</button>
      </div>
      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="SIL income" value={money(t.income)} sub={`${money(t.incomeReceived)} received`} />
          <Tile label="SIL worker cost" value={money(t.silCost)} sub={data.onCost > 1 ? `incl. on-costs ×${data.onCost}` : 'pay only'} />
          <Tile label="SIL margin" value={money(t.silMargin)} sub={t.silMarginPct == null ? '–' : `${t.silMarginPct}% of income`} tone={t.silMargin < 0 ? 'red' : null} />
          <Tile label="Unfunded SIL cost" value={money(t.unfundedSilCost)} sub="SIL worked outside Weekly funding" tone={t.unfundedSilCost > 0 ? 'amber' : null} />
        </div>
      )}
      <div className="card overflow-x-auto">
        {error ? <ErrorState onRetry={load} /> : !data ? <TableSkeleton rows={4} cols={6} /> : !grouped.length ? (
          <EmptyState icon={Home} title="No SIL activity in these weeks" />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">{group === 'week' ? 'Week · participant' : group === 'client' ? 'Participant' : group === 'house' ? 'House' : group === 'fy' ? 'Financial year' : 'Month'}</th>
                <th className="px-4 py-2.5 font-medium text-right">SIL income</th><th className="px-4 py-2.5 font-medium text-right">SIL cost</th>
                <th className="px-4 py-2.5 font-medium text-right">SIL margin</th><th className="px-4 py-2.5 font-medium text-right">Held</th>
                <th className="px-4 py-2.5 font-medium text-right">Irregular SIL</th><th className="px-4 py-2.5 font-medium text-right">Other services</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grouped.map((g) => {
                const margin = g.income - g.silCost;
                return (
                  <tr key={g.key}>
                    <td className="px-4 py-2.5 text-slate-900">{g.label}{group === 'week' && g.status && <span className="ml-2">{statusPill(g.status)}</span>}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{money(g.income)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{money(g.silCost)}{g.unfundedSilCost > 0 && <div className="text-[11px] text-amber-700">+{money(g.unfundedSilCost)} unfunded</div>}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${margin < 0 ? 'text-red-700' : 'text-slate-900'}`}>{money(margin)}<div className="text-[11px] font-normal text-slate-400">{pct(margin, g.income)}</div></td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{g.pendingIncome ? money(g.pendingIncome) : '–'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{g.irregularIncome || g.irregularCost ? `${money(g.irregularIncome)} / ${money(g.irregularCost)}` : '–'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{g.otherIncome || g.otherCost ? `${money(g.otherIncome)} / ${money(g.otherCost)}` : '–'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[12px] text-slate-500 max-w-[80ch]">
        Income is the weekly claim, counted in the week it covers. Cost is the pay of that week's SIL shifts, by the day the shift started, never the
        pay run. A shift shared by several clients is split between them. Held weeks show as pending, outside the totals. Irregular SIL and other
        services are income / cost.
      </p>
    </div>
  );
}

// ── Unpaid claims and invoices (spec 12) ───────────────────────────────────────────────────────
function UnpaidView({ stamp }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => { setError(false); try { const { data } = await api.get('/sil-weekly/unpaid'); setRows(data); } catch { setError(true); } }, []);
  useEffect(() => { load(); }, [load, stamp]);
  if (error) return <ErrorState onRetry={load} />;
  if (rows == null) return <TableSkeleton rows={3} cols={6} />;
  if (!rows.length) return <EmptyState icon={Check} title="Nothing unpaid" hint="Submitted NDIA claims and sent invoices waiting for payment show here." />;
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
          <tr><th className="px-4 py-2.5 font-medium">Invoice</th><th className="px-4 py-2.5 font-medium">Participant</th><th className="px-4 py-2.5 font-medium">Claim week</th>
            <th className="px-4 py-2.5 font-medium">Sent to</th><th className="px-4 py-2.5 font-medium text-right">Outstanding</th><th className="px-4 py-2.5 font-medium text-right">Days</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.invoice_id}>
              <td className="px-4 py-2.5 font-medium">{r.invoice_number}</td>
              <td className="px-4 py-2.5">{r.client}</td>
              <td className="px-4 py-2.5 tabular-nums">{au(r.week_start)}</td>
              <td className="px-4 py-2.5 text-slate-600">{r.payment_type === 'ndia' ? 'NDIA' : r.payment_type === 'plan_manager' ? `Plan manager${r.recipient ? `: ${r.recipient}` : ''}` : 'Participant / nominee'}</td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{money(r.total - r.paid)}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums ${r.overdue ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>{r.days_outstanding}{r.overdue && <div className="text-[11px] font-normal">overdue</div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Absences (spec 10: hold the claim for review after N+ days away) ───────────────────────────
function AbsencesView({ stamp }) {
  const [rows, setRows] = useState(null);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ clientId: '', fromDate: '', toDate: '', reason: 'hospital', notes: '' });
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const load = useCallback(async () => {
    try { const { data } = await api.get('/sil-weekly/absences'); setRows(data); } catch { setRows([]); }
    try {
      const { data } = await api.get('/sil-weekly/forecast');
      const seen = new Map(); for (const r of data || []) seen.set(r.clientId, r.client);
      setClients([...seen].map(([id, name]) => ({ id, name })));
    } catch { /* the list just stays empty */ }
  }, []);
  useEffect(() => { load(); }, [load, stamp]);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post('/sil-weekly/absences', form); toast.success('Absence recorded'); setForm({ clientId: '', fromDate: '', toDate: '', reason: 'hospital', notes: '' }); load(); }
    catch (err) { toast.error(errOf(err, 'Could not save the absence')); }
    finally { setBusy(false); }
  };
  const del = async (a) => {
    if (!await confirm(`Remove ${a.client}'s absence ${au(a.from_date)} to ${au(a.to_date)}?`, { title: 'Remove absence?', danger: true })) return;
    try { await api.delete(`/sil-weekly/absences/${a.id}`); load(); } catch { toast.error('Could not remove it'); }
  };
  return (
    <div className="space-y-3">
      <form onSubmit={add} className="card p-4 flex flex-wrap items-end gap-3">
        <div><label htmlFor="abs-client" className="block text-[12px] text-slate-500 mb-1">Participant</label>
          <select id="abs-client" className="input-field !py-1.5 text-sm" required value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
            <option value="">Choose…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div><label htmlFor="abs-from" className="block text-[12px] text-slate-500 mb-1">From</label>
          <input id="abs-from" type="date" className="input-field !py-1.5 text-sm" required value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} /></div>
        <div><label htmlFor="abs-to" className="block text-[12px] text-slate-500 mb-1">To</label>
          <input id="abs-to" type="date" className="input-field !py-1.5 text-sm" required min={form.fromDate || undefined} value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} /></div>
        <div><label htmlFor="abs-reason" className="block text-[12px] text-slate-500 mb-1">Reason</label>
          <select id="abs-reason" className="input-field !py-1.5 text-sm" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
            <option value="hospital">Hospital</option><option value="leave">Leave</option><option value="other">Other</option>
          </select></div>
        <div className="flex-1 min-w-[160px]"><label htmlFor="abs-notes" className="block text-[12px] text-slate-500 mb-1">Notes</label>
          <input id="abs-notes" className="input-field !py-1.5 text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        <button type="submit" className="btn-primary btn-sm" disabled={busy}><Plus size={13} aria-hidden="true" /> Record absence</button>
      </form>
      <p className="text-[12px] text-slate-500">
        Absences only hold a claim when "Hold the weekly claim for review" is switched on in Settings (off by default: a week with an approved SIL shift
        is claimed in full).
      </p>
      <div className="card overflow-x-auto">
        {rows == null ? <TableSkeleton rows={2} cols={5} /> : !rows.length ? <p className="p-4 text-sm text-slate-500">No absences recorded.</p> : (
          <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-2.5 font-medium">{a.client}</td>
                <td className="px-4 py-2.5 tabular-nums">{au(a.from_date)} to {au(a.to_date)}</td>
                <td className="px-4 py-2.5 capitalize">{a.reason}</td>
                <td className="px-4 py-2.5 text-slate-500">{a.notes}</td>
                <td className="px-4 py-2.5 text-right"><button type="button" className="btn-secondary btn-sm" onClick={() => del(a)} aria-label={`Remove ${a.client}'s absence`}><Trash2 size={12} aria-hidden="true" /></button></td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </div>
  );
}

// ── Admin settings (spec 10) ───────────────────────────────────────────────────────────────────
function SettingsView({ onSaved }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/sil-weekly/settings').then((r) => setS(r.data)).catch(() => setS({})); }, []);
  if (!s) return <TableSkeleton rows={4} cols={2} />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put('/sil-weekly/settings', s); setS(data); toast.success('Settings saved'); onSaved(); }
    catch (e) { toast.error(errOf(e, 'Only an admin can change these')); }
    finally { setBusy(false); }
  };
  const num = (k, label, hint, props = {}) => (
    <div>
      <label htmlFor={`sil-${k}`} className="block text-[13px] font-medium text-slate-700 mb-1">{label}</label>
      <input id={`sil-${k}`} type="number" className="input-field !py-1.5 text-sm w-32" value={s[k] ?? ''} onChange={(e) => set(k, e.target.value === '' ? '' : Number(e.target.value))} {...props} />
      {hint && <p className="text-[12px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
  const flag = (k, label, hint) => (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={!!s[k]} onChange={(e) => set(k, e.target.checked)} />
      <span><span className="text-[13px] font-medium text-slate-700">{label}</span>{hint && <span className="block text-[12px] text-slate-500">{hint}</span>}</span>
    </label>
  );
  return (
    <div className="card p-5 space-y-5 max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="sil-day" className="block text-[13px] font-medium text-slate-700 mb-1">Weekly claim run day</label>
          <select id="sil-day" className="input-field !py-1.5 text-sm w-44" value={s.claimRunDay} onChange={(e) => set('claimRunDay', Number(e.target.value))}>
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
          <p className="text-[12px] text-slate-500 mt-1">Claims the Monday to Sunday week just ended.</p>
        </div>
        {num('claimRunHour', 'Run from (hour, Sydney time)', '0 to 23. Default 7.', { min: 0, max: 23 })}
        {num('minApprovedShifts', 'Approved SIL shifts needed to claim the week', 'Default 1.', { min: 1 })}
        {num('paymentTermsDays', 'Invoice payment terms (days)', 'Plan- and self-managed invoices.', { min: 0 })}
        {num('lowBalanceWeeks', 'Low balance alert', 'When the SIL balance covers fewer weeks than this.', { min: 0 })}
        {num('absenceHoldDays', 'Hold the claim for review after an absence of (days)', '0 = off. Default off.', { min: 0, max: 7 })}
      </div>
      <div className="grid sm:grid-cols-2 gap-5 pt-4 border-t border-slate-100">
        <div>
          <label htmlFor="sil-roles" className="block text-[13px] font-medium text-slate-700 mb-1">Who can edit the weekly amount</label>
          <select id="sil-roles" className="input-field !py-1.5 text-sm w-60" value={s.editWeeklyRoles || 'admin_office'} onChange={(e) => set('editWeeklyRoles', e.target.value)}>
            <option value="admin_office">Admins and office users</option><option value="admin">Admins only</option>
          </select>
          <p className="text-[12px] text-slate-500 mt-1">Above the funding amount always needs an admin's approval.</p>
        </div>
        <div>
          <label htmlFor="sil-split" className="block text-[13px] font-medium text-slate-700 mb-1">Shared SIL shift cost in the P&L</label>
          <select id="sil-split" className="input-field !py-1.5 text-sm w-60" value={s.sharedCostSplit || 'equal'} onChange={(e) => set('sharedCostSplit', e.target.value)}>
            <option value="equal">Split equally between the clients</option><option value="ratio">Split by each client's ratio (1:2, 1:3…)</option>
          </select>
        </div>
      </div>
      <div className="space-y-3 pt-4 border-t border-slate-100">
        {flag('countShortNoticeCancel', 'A short-notice client cancellation counts toward the weekly trigger')}
        {flag('onCostsEnabled', 'Add on-costs to worker cost in the P&L', 'Superannuation, payroll tax and workers compensation at the rates below.')}
      </div>
      {s.onCostsEnabled && (
        <div className="grid sm:grid-cols-3 gap-5">
          {num('superPct', 'Superannuation %', null, { min: 0, step: 0.1 })}
          {num('payrollTaxPct', 'Payroll tax %', null, { min: 0, step: 0.1 })}
          {num('workersCompPct', 'Workers compensation %', null, { min: 0, step: 0.1 })}
        </div>
      )}
      <div className="pt-4 border-t border-slate-100 flex items-center gap-3">
        <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Check size={13} aria-hidden="true" />} Save settings</button>
        <p className="text-[12px] text-slate-500">After each Monday run the NDIA claim file is built and admins get a summary email.</p>
      </div>
    </div>
  );
}

// ── Audit log (spec 12) ────────────────────────────────────────────────────────────────────────
const ACTION_LABEL = {
  claiming_method: 'Claiming method changed', service_agreement: 'Service agreement recorded', weekly_amount_edit: 'Weekly amount edited',
  weekly_amount_reset: 'Weekly amount reset', weekly_amount_approved: 'Weekly amount approved', weekly_amount_declined: 'Weekly amount declined',
  claim_created: 'Claim created', claim_capped: 'Claim capped', claim_held: 'Claim held', claim_anyway: 'Held week claimed',
  claim_rejected: 'Claim rejected', claim_resubmitted: 'Claim resubmitted', claim_run: 'Claim run', ndia_file: 'NDIA file built',
  ndia_reconcile: 'NDIA results imported', agreement_missing: 'Weekly without an agreement on file', moved_from_weekly_fixed: 'Week moved from Weekly fixed', absence_added: 'Absence recorded', absence_removed: 'Absence removed', settings: 'Settings changed',
};
function detailText(a) {
  const d = a.detail || {};
  if (a.action === 'claiming_method') return `${d.from} to ${d.to}, from ${au(d.effectiveWeek)}`;
  if (a.action.startsWith('weekly_amount')) return `${d.oldAmount != null ? `${money(d.oldAmount)} to ` : ''}${d.newAmount != null ? money(d.newAmount) : money(d.amount)} from ${au(d.effectiveWeek)}${d.reason ? `: ${d.reason}` : ''}`;
  if (a.action.startsWith('claim_')) return [d.weekStart && `week of ${au(d.weekStart)}`, d.amount != null && money(d.amount), d.reason, d.message, d.reference].filter(Boolean).join(', ');
  if (a.action === 'ndia_file') return `${d.file}: ${plural(d.lines || 0, 'line')}, ${money(d.total)}`;
  if (a.action === 'ndia_reconcile') return `${d.paid} paid, ${d.rejected} rejected`;
  if (a.action === 'absence_added' || a.action === 'absence_removed') return `${au(d.fromDate)} to ${au(d.toDate)}${d.reason ? `, ${d.reason}` : ''}`;
  return '';
}
function AuditView({ stamp }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/sil-weekly/audit').then((r) => setRows(r.data)).catch(() => setRows([])); }, [stamp]);
  if (rows == null) return <TableSkeleton rows={4} cols={4} />;
  if (!rows.length) return <EmptyState icon={AlertTriangle} title="Nothing recorded yet" />;
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">
        {rows.map((a) => (
          <tr key={a.id} className="align-top">
            <td className="px-4 py-2.5 text-[12px] text-slate-500 whitespace-nowrap tabular-nums">{new Date(a.created_at).toLocaleString('en-AU')}</td>
            <td className="px-4 py-2.5 font-medium text-slate-900 whitespace-nowrap">{ACTION_LABEL[a.action] || a.action}</td>
            <td className="px-4 py-2.5">{a.client || ''}</td>
            <td className="px-4 py-2.5 text-slate-600">{detailText(a)}</td>
            <td className="px-4 py-2.5 text-[12px] text-slate-500 whitespace-nowrap">{a.by || 'Automatic'}</td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}
